import { QueryKey } from '@/configs/queryKeys';
import { BaseEntity, CreatorEntityType, UUID } from '@/types/types';
import { Attribute } from './AttributeApi';
import { ComponentAttributeValue } from './ComponentApi';
import { Accessory } from './AccessoryApi';
import ProductTypeApi, { ProductType } from './ProductTypeApi';
import OrderApi, { OrderItem } from './OrderApi';
import OfferApi, { OfferItem } from './OfferApi';
import EntityApi from './EntityApi';
import httpClient from './httpClient';

export interface ProductDimension {
	shortName: string;
	width: number;
	height: number;
}

export interface ProductAttribute {
	componentAttributeValue: ComponentAttributeValue | null;
	attribute: Attribute;
	value: string;
}

export interface Product extends BaseEntity {
	productAttributes: ProductAttribute[];
	productType: ProductType;
	price: number;
	netPrice: number;
	vatRate: number;
	discountedPrice: number;
	discountedNetPrice: number;
	dimensions: ProductDimension[];
	accessories: Accessory[];
	comment: string;
	surface: number;
	weight: number;
	clientId: UUID | null;
}

class ProductApi extends EntityApi<Product> {
	constructor() {
		super(QueryKey.products);
		this.getWithItem = this.getWithItem.bind(this);
		this.updateDimensions = this.updateDimensions.bind(this);
	}

	async getWithItem({
		productId,
		entityItemId,
		entityType,
	}: {
		productId: UUID;
		entityItemId: UUID | null;
		entityType: CreatorEntityType;
	}) {
		let itemQuery: Promise<OrderItem> | Promise<OfferItem> | null = null;
		if (entityItemId)
			switch (entityType) {
				case CreatorEntityType.ORDER:
					itemQuery = OrderApi.getOrderItem(entityItemId);
					break;
				case CreatorEntityType.OFFER:
					itemQuery = OfferApi.getOfferItem(entityItemId);
					break;
			}

		const [product, entityItem] = await Promise.all([
			super.mutationGetOne(productId),
			itemQuery,
		]);

		const productType = await ProductTypeApi.mutationGetOne(
			product.productType.id
		);

		return {
			productType,
			product,
			entityItem,
		};
	}

	async updateDimensions({
		id,
		dimensions,
	}: Pick<Product, 'id' | 'dimensions' | 'clientId'>) {
		const response = await httpClient.patch<Product>(
			`${this.queryKey}/${id}/dimensions`,
			{
				dimensions,
			}
		);

		return response.data;
	}
}

export default new ProductApi();
